import type { ModelInstance } from 'miragejs/-types'
import type { Collection } from 'miragejs'
import { filterable, pageable } from './../utils.js'
import type { MockServer, MirageServer } from './../server.js'
import type { SchemaAttributes } from '../../types.js'

export type ConsultationNoteWithRelations =
  SchemaAttributes<'ConsultationNote'> &
    ModelInstance & {
      consultation: ConsultationWithRelations
      patient: ModelInstance<SchemaAttributes<'Patient'>>
    }

export type ConsultationWithRelations = SchemaAttributes<'Consultation'> &
  ModelInstance & {
    patients: Collection<ModelInstance<SchemaAttributes<'Patient'>>>
    client: ModelInstance<SchemaAttributes<'Client'>>
  }

const emptyResponse = {
  data: [],
  meta: {
    pagination: {
      count: 0,
    },
  },
}

export function routes(server: MockServer) {
  server.get(
    '/consultations/:parent_lookup_consultation/previous_consultation_notes',
    function (this: MirageServer, schema, request) {
      const currentConsultationId = request.params.parent_lookup_consultation

      const currentConsultation = schema.find(
        'consultation',
        currentConsultationId,
      ) as ConsultationWithRelations | null

      if (!currentConsultation) {
        return emptyResponse
      }

      const patientIds = currentConsultation.patients.models
        .map(
          (patient: ModelInstance<SchemaAttributes<'Patient'>>) => patient.id,
        )
        .filter((id): id is string => Boolean(id))

      if (patientIds.length === 0) {
        return emptyResponse
      }

      const allConsultationNotes = schema.all('consultationNote')

      const previousNoteIds: string[] = []
      allConsultationNotes.models.forEach((note: ModelInstance) => {
        const typedNote = note as unknown as ConsultationNoteWithRelations

        if (typedNote.consultation?.id === currentConsultationId) {
          return
        }

        if (!typedNote.consultation) {
          return
        }

        const noteConsultationPatientIds =
          typedNote.consultation.patients.models
            .map(
              (patient: ModelInstance<SchemaAttributes<'Patient'>>) =>
                patient.id,
            )
            .filter((id): id is string => Boolean(id))

        const hasSharedPatient = noteConsultationPatientIds.some(
          (patientId: string) => patientIds.includes(patientId),
        )

        if (hasSharedPatient && typedNote.id) {
          previousNoteIds.push(typedNote.id)
        }
      })

      const previousNotesCollection = schema.where(
        'consultationNote',
        (note: ModelInstance) => {
          const typedNote = note as unknown as ConsultationNoteWithRelations
          return typedNote.id ? previousNoteIds.includes(typedNote.id) : false
        },
      )

      // Apply filters
      const filtered = filterable(previousNotesCollection, request, {
        search: (item, value) =>
          (item.attrs.text || '')
            .toLowerCase()
            .includes((value || '').toLowerCase()),
        consultation: (item, value) => item.attrs.consultationId === value,
        custom_type: (item, value) => item.attrs.customTypeId === value,
        general: (item) => (!item.attrs.customTypeId ? true : false),
        draft: (item) => item.attrs.draft,
        is_active: (item, value) => {
          const isActive = !item.attrs.archived_at
          return value === 'false' ? !isActive : isActive
        },
        user: (item, value) =>
          item.attrs.createdUserId === value ||
          item.attrs.modifiedUserId === value,
      })

      // Apply pagination
      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}
